import compareVersions from 'compare-versions'
import moment from 'moment-timezone'
import Base from '@/resources/Base'
import { formatFromNow, getRuntimeText } from '@/utilities/datetime.helper'
import { getHostTrailRiskLevel } from '@/utilities/riskscore'
import { AgentStateEnum, HostStatusEnum } from '@/constants/host-status.constants'
import { CriticalityLevelEnum, CriticalityEnum } from '@/constants/criticality.constants'
import { NANOSECONDS_TO_MILLISECONDS, INACTIVE_AGENT_HOURS } from '@/constants/datetime.constants'
import { ipv4Re, ipv6Re, windowsDHCPFailRe } from '@/utilities/regex/ipaddress'
import { getUserFriendlyAgentLabel } from '@/utilities/agent.helper'

export default class Agent extends Base {
  constructor (uuid = null, attributes = {}, options = {}) {
    super(uuid, attributes, options)
    this.containers = []
    this.display = true
    this.decommissionEligibile = false
    this.upgradeable = false
    this.needsAttention = false
    this.upgradeText = 'Latest'
    if (!this.criticality || _.isEmpty(this.criticality)) {
      this.criticality = CriticalityLevelEnum.REGULAR
    }
    this.agentDetails = {}
    if (this.managed) {
      _.merge(this.agentDetails, this.agentInfo, this.instanceDiscoveryRecord)
    } else {
      _.merge(this.agentDetails, this.instanceDiscoveryRecord, {
        system: {
          os: this.instanceDiscoveryRecord.platform
        }
      })
    }
    // merge system (os info) info into top level accessor for backwards compatibility
    if (_.isEmpty(this.agentDetails.system)) {
      this.system = {
        os: ''
      }
    } else {
      this.system = this.agentDetails.system
    }
    this.setAgentInfo(this.agentDetails)
  }

  setAgentInfo (agentInfo) {
    // managed | unmanaged | unmanaged and name is null
    let managedAgentId = agentInfo.agentId
    agentInfo.agentId = agentInfo.agentId || agentInfo.name || ''
    if (_.isEmpty(this.agentId)) {
      this.agentId = agentInfo.agentId
    }
    agentInfo.aggregatedRiskScore = agentInfo.aggregatedRiskScore || 0

    // convert createTime to ms if necessary (backend should be sending everything as nanosec)
    if (agentInfo.createTime && !moment(agentInfo.createTime).isValid()) {
      agentInfo.createTime = agentInfo.createTime * NANOSECONDS_TO_MILLISECONDS
    } else {
      // createTime will come from launchTime for unprotected vms
      if (!moment(agentInfo.launchTime).isValid()) {
        agentInfo.createTime = agentInfo.launchTime * NANOSECONDS_TO_MILLISECONDS
      }
    }
    if (!this.customHostname) {
      // assign to hostname (if exists) if no custom hostname
      if (agentInfo.hostnameOverride) {
        this.customHostname = agentInfo.hostnameOverride
      } else if (agentInfo.hostname) {
        this.customHostname = agentInfo.hostname
      } else if (agentInfo.name) {
        this.customHostname = agentInfo.name
      } else {
        // else 'UNNAMED'
        this.customHostname = 'UNNAMED'
      }
    }
    if (agentInfo.tagMap) {
      this.tags = _.chain(agentInfo.tagMap)
        .values()
        .uniq()
        .omitBy(_.isNil)
        .omitBy(_.isEmpty)
        .values()
        .value()
    }
    this.setSource()
    this.setAgentLabel(agentInfo.agentId)
    if (managedAgentId) {
      this.setAgentNumber(managedAgentId)
    }
    this.setRiskLevel(agentInfo.aggregatedRiskScore)
    this.setAtRisk(this.numProgressions)
    // set initial state of Agent based on status + score
    this.setState(agentInfo.agentStatus, agentInfo.aggregatedRiskScore, agentInfo.lastStatusChangeTime)
    // select ipv4 address for local IP
    this.setLocalIP(agentInfo.privateIPs)
    this.checkUpgradeable(agentInfo.agentVersion, this.iqhubVersion)
    this.setNeedsAttention(agentInfo)
    this.setProtected(agentInfo)

    // set uptime for agent
    if (this.discovered) {
      // if agent is discovered
      if (this.instanceDiscoveryRecord.state === 'stopped') {
        this.setUpTime() // running time = 'N/A'
      } else {
        if (_.isNull(this.instanceDiscoveryRecord.launchTime)) {
          this.upTime = 'N/A'
        } else {
          // running time = current time - launch time
          !moment(this.instanceDiscoveryRecord.launchTime).isValid()
            ? this.setUpTime(this.instanceDiscoveryRecord.launchTime * NANOSECONDS_TO_MILLISECONDS)
            : this.setUpTime(this.instanceDiscoveryRecord.launchTime)
        }
      }
    } else {
      // if agent is managed
      if (agentInfo.agentStatus.toLowerCase() === 'offline') {
        this.setUpTime() // running time = 'N/A'
      } else {
        // running time = current time - create time
        !moment(agentInfo.createTime).isValid()
          ? this.setUpTime(agentInfo.createTime * NANOSECONDS_TO_MILLISECONDS)
          : this.setUpTime(agentInfo.createTime)
      }
    }
  }

  setAgentLabel (label) {
    this.agentLabel = getUserFriendlyAgentLabel(label)
  }

  getAgentLabel () {
    if (this.customHostname !== 'UNNAMED') {
      return this.customHostname
    }
    return this.agentLabel
  }

  setAgentNumber (agentId) {
    if (agentId.includes('-')) {
      this.agentNumber = agentId.split('-')[1]
    } else {
      this.agentNumber = agentId
    }
  }

  /**
   * Needs Attention - if
   * Agent needed || Progression exist || Offline || Upgradeable
   *
   */
  setNeedsAttention (agentInfo) {
    if (
      this.managed === false ||
      this.upgradeable ||
      this.numProgressions > 0 ||
      agentInfo.agentStatus === AgentStateEnum.OFFLINE
    ) {
      this.needsAttention = true
    }
  }

  /**
   * Protected states
   * Yes -> Installed & Online
   * Offline -> Installed & Offline
   * No -> No agent
   * Update -> Upgrade needed
   */
  setProtected (agentInfo) {
    if (!this.protectionOn) {
      this.protected = 'No'
    } else if (this.protectionOn && agentInfo.agentStatus === AgentStateEnum.ONLINE) {
      this.protected = 'Yes'
    } else if (this.protectionOn && agentInfo.agentStatus === AgentStateEnum.OFFLINE) {
      this.protected = 'Offline'
    } else if (this.protectionOn && this.upgradeable) {
      this.protected = 'Upgrade'
    }
  }

  setUpTime (time) {
    if (!isNaN(time)) {
      let uptime = formatFromNow(time).split(' ')[0]
      this.upTime = getRuntimeText(uptime)
    } else {
      this.upTime = 'N/A'
    }
  }

  setSource () {
    // Q2 - only supporting AWS
    // If vm is not discovered through AWS, it is connected through a confluera agent
    if (this.discovered) {
      this.discovered = 'discovered'
      switch (this.discoveryAccountType) {
        case 'AZURE':
          this.source = 'AZURE'
          break
        case 'GCP':
          this.source = 'GCP'
          break
        default:
          this.source = 'AWS'
      }
    } else {
      this.source = 'CONFLUERA'
    }
  }

  /**
   * Node size used in topology based on criticality level
   * Critical: 36px
   * Important: 24px
   * Regular/Normal: 12px
   */
  getNodeSize () {
    switch (this.criticality) {
      case CriticalityLevelEnum.CRITICAL:
        return CriticalityEnum.properties.critical.size
      case CriticalityLevelEnum.IMPORTANT:
        return CriticalityEnum.properties.important.size
      default:
        return CriticalityEnum.properties.regular.size
    }
  }

  getState () {
    return this.getState
  }

  setState (status, score, lastStatusChangeTime) {
    if (status === AgentStateEnum.ONLINE && score === 0) {
      this.state = HostStatusEnum.properties.safe.state
    } else if (status === AgentStateEnum.ONLINE && score > 0) {
      this.state = HostStatusEnum.properties.at_risk.state
    } else if (status === AgentStateEnum.DECOMMISSIONED) {
      this.state = HostStatusEnum.properties.decommissioned.state
    } else {
      this.state = HostStatusEnum.properties.offline.state
      this.setDecommissionEligibility(status, lastStatusChangeTime)
    }
  }

  setLocalIP (privateIPs = []) {
    this.localIPs = []
    if (!_.isEmpty(privateIPs)) {
      privateIPs.forEach(ip => {
        if (ipv4Re.test(ip) && !windowsDHCPFailRe.test(ip)) {
          this.localIPs.push(ip)
        }
      })
    }
    if (this.localIPs.length === 0) {
      if (!_.isEmpty(privateIPs)) {
        privateIPs.forEach(ip => {
          if (ipv6Re.test(ip)) {
            this.localIPs.push(ip)
          }
        })
      }
    }
  }

  setRiskLevel (riskScore) {
    this.riskLevel = getHostTrailRiskLevel(riskScore)
  }

  setAtRisk (progressions) {
    this.atRisk = progressions > 0
  }

  /**
   *
   * Host is eligible to be decommissioned if Status is OFFLINE
   *    && lastStatusChangeTime is > 24hrs
   */
  setDecommissionEligibility (status, lastStatusEventTimestamp) {
    if (!moment(lastStatusEventTimestamp).isValid()) {
      lastStatusEventTimestamp = lastStatusEventTimestamp * NANOSECONDS_TO_MILLISECONDS
    }
    let timeDelta = moment().diff(moment(lastStatusEventTimestamp), 'hours')
    if (status === AgentStateEnum.OFFLINE && timeDelta > INACTIVE_AGENT_HOURS) {
      this.decommissionEligibile = true
      this.state = HostStatusEnum.properties.decommissionable.state
    }
  }

  /**
   * Mark upgrade needed only for major versions.
   * 2.2 vs 2.3 -> upgrade needed
   * 2.2.1 vs 2.2.2 -> no upgrade needed
   *
   * @param {*} agentVersion
   * @param {*} iqhubVersion
   * @returns
   */
  checkUpgradeable (agentVersion, iqhubVersion) {
    if (
      this.state === AgentStateEnum.OFFLINE ||
      this.state === AgentStateEnum.DECOMMISSIONED ||
      this.state === AgentStateEnum.DECOMMISSIONABLE ||
      _.isEmpty(iqhubVersion)
    ) {
      this.upgradeText = 'Offline'
      return
    }
    if (_.isEmpty(agentVersion)) {
      this.upgradeable = true
      this.upgradeText = 'Upgradeable'
      return
    }

    let agentVersionMajorIdx = agentVersion.indexOf('.', agentVersion.indexOf('.') + 1)
    let agentVersionCompare = agentVersion.substring(0, agentVersionMajorIdx)

    let iqhubVersionMajorIdx = iqhubVersion.indexOf('.', iqhubVersion.indexOf('.') + 1)
    let iqhubVersionCompare = iqhubVersion.substring(0, iqhubVersionMajorIdx)

    if (!_.isEmpty(agentVersion) && compareVersions.compare(agentVersionCompare, iqhubVersionCompare, '<')) {
      this.upgradeable = true
      this.upgradeText = 'Upgradeable'
    }
  }

  setContainerInfo (containerInfo) {
    this.containers = containerInfo
  }
}



// WEBPACK FOOTER //
// ./src/resources/models/Agent.js